import React, { useEffect, useState } from 'react'
import { Show } from '@refinedev/antd'
import { useShow } from '@refinedev/core'
import { App, Button, Card, Col, Form, InputNumber, Modal, Row, Space, Tooltip } from 'antd'
import { brand } from '../../theme/brand'
import { Pill, ProgressBar } from '../../theme/listkit'
import { supabaseClient } from '../../lib/supabaseClient'
import { groupsAdminErrorMessage } from './adminErrors'
import { categoryLabel } from './categoryOptions'
import { groupStatusTone, privacyLabel, type Group } from './list'
import { GroupMembersTable } from './GroupMembersTable'

interface LaunchStatus {
  done_count: number
  is_complete: boolean
  has_cover_and_about: boolean
  has_welcome_message: boolean
  has_first_meetup: boolean
  invites_sent: number
  invite_goal: number
  member_count: number
  member_goal: number
}

const headStyle: React.CSSProperties = { fontFamily: brand.fDisplay, fontWeight: 700, color: brand.ink }
const labelStyle: React.CSSProperties = {
  fontFamily: brand.fSans,
  fontWeight: 700,
  fontSize: 11,
  letterSpacing: '.09em',
  textTransform: 'uppercase',
  color: brand.gold,
  marginBottom: 4,
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={labelStyle}>{label}</div>
      <div style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 13.5, color: brand.ink }}>{children}</div>
    </div>
  )
}

/**
 * Group detail — read-only summary plus the three status actions
 * (Suspend/Restore/Archive, all via `set_group_status`) and the member roster.
 * Editing fields happens on the Edit page; status changes are deliberately kept
 * off that form (see edit.tsx's doc comment) and live here as explicit,
 * confirmed actions.
 */
export function GroupShow() {
  const { query } = useShow<Group>()
  const { data, isLoading } = query
  const record = data?.data

  const { message, modal } = App.useApp()
  const [busy, setBusy] = useState<null | 'active' | 'suspended' | 'archived'>(null)
  const [launch, setLaunch] = useState<LaunchStatus | null>(null)
  const [featureBusy, setFeatureBusy] = useState(false)
  const [featureOpen, setFeatureOpen] = useState(false)
  const [overrideMode, setOverrideMode] = useState(false)
  const [featureForm] = Form.useForm<{ rank: number }>()

  useEffect(() => {
    if (!record?.id || record.kind === 'class_chapter') return
    let cancelled = false
    supabaseClient
      .from('group_launch_status')
      .select('*')
      .eq('group_id', record.id)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled) setLaunch((data as LaunchStatus | null) ?? null)
      })
    return () => {
      cancelled = true
    }
  }, [record?.id, record?.kind])

  async function featureGroup(rank: number | null, override: boolean) {
    if (!record) return
    setFeatureBusy(true)
    try {
      const { error } = await supabaseClient.rpc('feature_group', { p_group: record.id, p_rank: rank, p_override: override })
      if (error) throw error
      message.success(rank === null ? 'Removed from Featured.' : `Featured at rank ${rank}.`)
      setFeatureOpen(false)
      featureForm.resetFields()
      query.refetch()
    } catch (e) {
      message.error(groupsAdminErrorMessage(e))
    } finally {
      setFeatureBusy(false)
    }
  }

  function submitFeatureModal() {
    featureForm.validateFields().then((v) => {
      if (overrideMode) {
        modal.confirm({
          title: 'Feature this group anyway?',
          content: 'The launch checklist isn’t complete yet. Featuring it now overrides that gate.',
          okText: 'Feature anyway',
          okButtonProps: { danger: true },
          onOk: () => featureGroup(v.rank, true),
        })
      } else {
        void featureGroup(v.rank, false)
      }
    })
  }

  async function setStatus(status: 'active' | 'suspended' | 'archived') {
    if (!record) return
    setBusy(status)
    try {
      const { error } = await supabaseClient.rpc('set_group_status', { p_group: record.id, p_status: status })
      if (error) throw error
      message.success('Group updated')
      query.refetch()
    } catch (e) {
      message.error(groupsAdminErrorMessage(e))
    } finally {
      setBusy(null)
    }
  }

  function confirmStatus(status: 'active' | 'suspended' | 'archived', title: string, okText: string) {
    if (!record) return
    const base = `${record.name} will be ${status === 'active' ? 'visible and active' : status} for members.`
    modal.confirm({
      title,
      // Archive now also freezes the group's Stream channel (channelFrozen(),
      // stream-sync-group-membership / stream-ensure-group-channel) — nobody
      // can post while suspended or archived, so the owner should know before
      // confirming.
      content: status === 'archived' ? `${base}…and freezes the group chat.` : base,
      okText,
      okButtonProps: { danger: status !== 'active' },
      onOk: () => setStatus(status),
    })
  }

  const isChapter = record?.kind === 'class_chapter'
  const status = record?.status
  const canSuspend = status === 'active'
  const canRestore = status === 'suspended' || status === 'archived'
  const canArchive = status === 'active' || status === 'suspended'

  return (
    <Show isLoading={isLoading} title={false} breadcrumb={false} headerButtons={() => null}>
      <div
        style={{
          background: `linear-gradient(135deg, ${brand.navy} 0%, ${brand.jaguar} 100%)`,
          borderRadius: 14,
          padding: '22px 24px',
          marginBottom: 18,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 18,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div
            style={{
              fontFamily: brand.fSans,
              fontWeight: 700,
              fontSize: 11,
              letterSpacing: '.09em',
              textTransform: 'uppercase',
              color: brand.gold,
              marginBottom: 6,
            }}
          >
            {isChapter ? 'Class chapter' : 'Group'}
          </div>
          <div style={{ fontFamily: brand.fDisplay, fontWeight: 700, fontSize: 24, color: '#fff' }}>
            {record?.name ?? '—'}
          </div>
          <Space size={8} style={{ marginTop: 10 }} wrap>
            {record && <Pill tone={groupStatusTone(record.status)}>{record.status}</Pill>}
            {record && <Pill tone="neutral">{privacyLabel(record.privacy)}</Pill>}
            {record && <Pill tone="jaguar">{categoryLabel(record.category)}</Pill>}
          </Space>
        </div>
        {record && !isChapter && (
          <Space wrap>
            {record.featured_rank == null ? (
              <Tooltip title={launch?.is_complete === false ? 'Complete the launch checklist to feature this group' : undefined}>
                <Button
                  data-testid="group-feature"
                  loading={featureBusy}
                  disabled={featureBusy || launch?.is_complete === false}
                  onClick={() => {
                    setOverrideMode(false)
                    setFeatureOpen(true)
                  }}
                >
                  Feature
                </Button>
              </Tooltip>
            ) : (
              <Button data-testid="group-unfeature" loading={featureBusy} disabled={featureBusy} onClick={() => featureGroup(null, false)}>
                Unfeature
              </Button>
            )}
            {launch && !launch.is_complete && record.featured_rank == null && (
              <Button
                data-testid="group-feature-override"
                disabled={featureBusy}
                onClick={() => {
                  setOverrideMode(true)
                  setFeatureOpen(true)
                }}
              >
                Override
              </Button>
            )}
            {canSuspend && (
              <Button
                data-testid="group-suspend"
                danger
                loading={busy === 'suspended'}
                disabled={busy !== null}
                onClick={() => confirmStatus('suspended', 'Suspend this group?', 'Suspend')}
              >
                Suspend
              </Button>
            )}
            {canRestore && (
              <Button
                data-testid="group-restore"
                loading={busy === 'active'}
                disabled={busy !== null}
                onClick={() => confirmStatus('active', 'Restore this group?', 'Restore')}
              >
                Restore
              </Button>
            )}
            {canArchive && (
              <Button
                data-testid="group-archive"
                danger
                loading={busy === 'archived'}
                disabled={busy !== null}
                onClick={() => confirmStatus('archived', 'Archive this group?', 'Archive')}
              >
                Archive
              </Button>
            )}
          </Space>
        )}
      </div>

      <Card title={<span style={headStyle}>About</span>} style={{ marginBottom: 16 }}>
        <Row gutter={24}>
          <Col xs={24} md={12}>
            <Field label="Tagline">{record?.tagline ?? '—'}</Field>
            <Field label="Home city">{record?.home_city ?? (record?.is_virtual_first ? 'Virtual' : '—')}</Field>
            <Field label="Cadence">{record?.cadence_label ?? '—'}</Field>
          </Col>
          <Col xs={24} md={12}>
            <Field label="Members">{(record?.member_count ?? 0).toLocaleString()}</Field>
            <Field label="Featured rank">{record?.featured_rank ?? '—'}</Field>
            <Field label="Created">{record ? new Date(record.created_at).toLocaleString() : '—'}</Field>
          </Col>
        </Row>
        {record?.about && <Field label="About">{record.about}</Field>}
      </Card>

      {launch && !isChapter && (
        <Card title={<span style={headStyle}>Launch checklist</span>} style={{ marginBottom: 16, maxWidth: 720 }}>
          <div style={{ marginBottom: 10 }}>
            {`${launch.done_count} of 5 done`}
            <ProgressBar value={launch.done_count} max={5} />
          </div>
          <Space direction="vertical" size={4}>
            <Pill tone={launch.has_cover_and_about ? 'green' : 'neutral'}>Cover and about</Pill>
            <Pill tone={launch.has_welcome_message ? 'green' : 'neutral'}>Welcome posted</Pill>
            <Pill tone={launch.has_first_meetup ? 'green' : 'neutral'}>First meetup</Pill>
            <Pill tone={launch.invites_sent >= launch.invite_goal ? 'green' : 'neutral'}>
              {`Invites sent (${launch.invites_sent}/${launch.invite_goal})`}
            </Pill>
            <Pill tone={launch.member_count >= launch.member_goal ? 'green' : 'neutral'}>
              {`Members (${launch.member_count}/${launch.member_goal})`}
            </Pill>
          </Space>
        </Card>
      )}

      {record && <GroupMembersTable groupId={record.id} groupKind={record.kind} />}

      {record && (
        <div
          data-testid="group-delete-note"
          style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 11.5, color: brand.sub, marginTop: 10, maxWidth: 720 }}
        >
          Deleting a group in the database removes its photos, album and chat for good. Classroom chapters with memories
          cannot be deleted.
        </div>
      )}

      <Modal
        title={overrideMode ? 'Feature anyway' : 'Feature this group'}
        open={featureOpen}
        onCancel={() => {
          setFeatureOpen(false)
          featureForm.resetFields()
        }}
        okText={overrideMode ? 'Continue' : 'Feature'}
        okButtonProps={{ loading: featureBusy }}
        onOk={submitFeatureModal}
      >
        <Form form={featureForm} layout="vertical" initialValues={{ rank: 1 }}>
          <Form.Item name="rank" label="Rank" rules={[{ required: true, type: 'number', min: 0 }]}>
            <InputNumber min={0} style={{ width: '100%' }} data-testid="group-feature-rank" />
          </Form.Item>
        </Form>
      </Modal>
    </Show>
  )
}
